/**
 * SettingsGroupPage — reusable index page for each settings group.
 * Rendered at /prioritization/settings/:groupKey via PrioritizationApp.
 * Receives the group key as a prop and renders its card grid.
 */

import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { isModuleEnabled, useWorkspaceModules } from '../../../lib/workspaceModulesService';
import {
  getSettingsGroup,
  filterVisibleSettingsItems,
  groupSettingsItemsBySubsection,
  type SettingsAccessContext,
} from './settingsConfig';
import type { SettingsItem } from './settingsConfig';
import {
  settingsLinkCard,
  settingsLinkCardDesc,
  settingsLinkCardIcon,
  settingsLinkCardTitle,
  settingsMutedBody,
  settingsSubsectionTitle,
} from './settingsUi';

interface Props {
  groupKey: string;
}

function SettingsCard({ item }: { item: SettingsItem }) {
  const Icon = item.icon;
  return (
    <Link to={item.href} className={`${settingsLinkCard} p-4`}>
      <div className={`${settingsLinkCardIcon} h-9 w-9`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className={settingsLinkCardTitle}>{item.name}</h3>
        <p className={settingsLinkCardDesc}>{item.description}</p>
      </div>
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-[var(--text-ter)] dark:text-[var(--text-ter)] group-hover:text-primary-400 dark:group-hover:text-[var(--text-sec)] mt-0.5" />
    </Link>
  );
}

export default function SettingsGroupPage({ groupKey }: Props) {
  const currentRole = useUserStore((s) => s.currentRole);
  const canAny = useUserStore((s) => s.canAny);
  const isPlatformAdmin = currentRole === 'platform-admin' || currentRole === 'system-admin';

  const group = getSettingsGroup(groupKey);

  const workspaceFeatures = useWorkspaceModules();
  const ctx: SettingsAccessContext = useMemo(
    () => ({
      canAny,
      isPlatformAdmin,
      isModuleEnabled: (moduleKey: string) => isModuleEnabled(workspaceFeatures, moduleKey),
    }),
    [canAny, isPlatformAdmin, workspaceFeatures],
  );
  const visibleItems = filterVisibleSettingsItems(group?.items ?? [], ctx);
  const sections = groupSettingsItemsBySubsection(visibleItems);

  if (!group) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] pp-settings">
        Settings group "{groupKey}" not found.
      </div>
    );
  }

  const GroupIcon = group.icon;

  const groupAllowed = !group.adminOnly || isPlatformAdmin || visibleItems.length > 0;
  if (!groupAllowed || visibleItems.length === 0) {
    return (
      <div className="space-y-6 pp-settings">
        <PageHeader
          icon={<GroupIcon className="h-4 w-4 text-primary-600 dark:text-[var(--text-sec)]" />}
          iconBg="bg-primary-50"
          title={group.title}
          subtitle={group.description}
        />
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className={settingsMutedBody}>You don't have access to any settings in this group.</p>
          <Link
            to="/prioritization/settings"
            className="mt-3 text-sm font-medium text-primary-600 dark:text-[var(--text-sec)] hover:underline"
          >
            Back to Settings
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pp-settings">
      <PageHeader
        icon={<GroupIcon className="h-4 w-4 text-primary-600 dark:text-[var(--text-sec)]" />}
        iconBg="bg-primary-50"
        title={group.title}
        subtitle={group.description}
      />

      <div className="space-y-8">
        {sections.map(({ subsection, items }) => (
          <div key={subsection ?? 'default'}>
            {subsection && (
              <h3 className={settingsSubsectionTitle}>{subsection}</h3>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {items.map((item) => (
                <SettingsCard key={item.href} item={item} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
